import { faClock, faEnvelope, faMegaphone, faUser, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import moment from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

import { isSenderReadyForSend } from '../isSenderReadyForSend';
import { EmailCampaignFormValues } from '../types';

import styles from './CampaignReviewSummary.module.scss';

interface CampaignReviewSummaryProps {
    values: EmailCampaignFormValues;
    /**
     * Resolved recipient count post-exclusions — same number computed by the
     * wizard's parent. We never recompute it here so the modal and the panel
     * always show identical figures.
     */
    recipientTotal: number;
    /**
     * Toggle the "Send" row. Default `true` (modal needs it).
     *   - Modal      → true  (renders alongside Price + Adyen)
     *   - Step 3 panel → false (Send Time already lives above as form controls)
     */
    includeSendTime?: boolean;
    className?: string;
}

/**
 * Read-only summary of the campaign — rendered in Step 3's left panel
 * (`CampaignSummaryPanel`) so the user can sanity-check before opening the
 * commit modal. The commit modal itself no longer re-shows this block to
 * avoid duplication: it now uses a one-line confirmation prompt instead.
 *
 * Card layout: each row is `[icon + label] | value` with a subtle divider,
 * matching the visual rhythm of other V3 read-only cards in the wizard.
 */
export const CampaignReviewSummary: React.FC<CampaignReviewSummaryProps> = ({
    values,
    recipientTotal,
    includeSendTime = true,
    className
}) => {
    const { clientTags } = useSelector((state: RootState) => state.masterData.data);
    const sender = useSelector((state: RootState) => state.emailMarketingSender.data);

    const recipientsCriteria = humanizeCriteria(values, clientTags?.positive ?? []);

    // Effective From address — matches what the backend will actually use at
    // send time. Under the mandatory-sender rule the only sendable state is a
    // fully verified custom sender; anything else surfaces "Not configured"
    // rather than a Cuddles-default fallback that would misrepresent what
    // gets sent (nothing — the send is blocked upstream).
    const senderReady = isSenderReadyForSend(sender);
    const effectiveSenderEmail = senderReady ? sender!.senderEmail : null;

    const fromDisplay = effectiveSenderEmail
        ? values.senderName
            ? `${values.senderName} <${effectiveSenderEmail}>`
            : effectiveSenderEmail
        : 'Not configured';

    return (
        <dl className={clsx(styles.root, className)}>
            <SummaryRow
                icon={faMegaphone}
                label="Campaign"
                value={values.campaignName || '—'}
            />
            <SummaryRow
                icon={faUsers}
                label="Recipients"
                value={
                    <>
                        <div>
                            {recipientTotal.toLocaleString()}{' '}
                            {Array.isArray(values.selectedClientIds) ? 'specific clients' : 'Clients'}
                        </div>
                        {/* Criteria + exclusion sub-lines only make sense in
                            exclude mode. Include mode bypasses criteria on
                            the wire, so showing "All clients — 5 excluded"
                            here would lie about what gets sent. */}
                        {!Array.isArray(values.selectedClientIds) && (
                            <>
                                <div className={styles.subLine}>{recipientsCriteria}</div>
                                {values.excludedClientIds.length > 0 && (
                                    <div className={styles.subLine}>
                                        {values.excludedClientIds.length} manually excluded
                                    </div>
                                )}
                            </>
                        )}
                    </>
                }
            />
            <SummaryRow
                icon={faEnvelope}
                label="Subject"
                value={values.subject || '—'}
            />
            <SummaryRow
                icon={faUser}
                label="From"
                value={fromDisplay}
            />
            {includeSendTime && (
                <SummaryRow
                    icon={faClock}
                    label="Send"
                    value={formatSendTimeLabel(values)}
                />
            )}
        </dl>
    );
};

// ---------------------------------------------------------------------------
// Internal helpers — shared between this component and any future consumer
// that needs to render the same humanizations.
// ---------------------------------------------------------------------------

const SummaryRow: React.FC<{ icon: FontAwesomeIconProps['icon']; label: string; value: React.ReactNode }> = ({
    icon,
    label,
    value
}) => (
    <div className={styles.row}>
        <div className={styles.labelCol}>
            <FontAwesomeIcon
                icon={icon}
                className={styles.icon}
            />
            <dt className={styles.label}>{label}</dt>
        </div>
        <dd className={styles.value}>{value}</dd>
    </div>
);

/**
 * Turn the wizard form values into a short, human-readable sentence describing
 * who will receive the campaign. Each `recipientType` maps to its own label —
 * no dynamic population prefix needed since the recipient mode itself already
 * conveys the audience.
 */
const humanizeCriteria = (
    values: EmailCampaignFormValues,
    positiveTags: Array<{ id: string; name: string }>
): string => {
    switch (values.recipientType) {
        case 'all_profiles':
            return 'All Profiles (Clients + Leads)';
        case 'all_clients':
            return 'All Clients';
        case 'leads':
            return 'All Leads';
        case 'client_groups': {
            const names = values.selectedTagIds
                .map(id => positiveTags.find(t => t.id === id)?.name)
                .filter((name): name is string => Boolean(name));
            if (names.length === 0) return 'Clients by Tag';
            return `Clients by Tag (${names.join(', ')})`;
        }
        default:
            return '—';
    }
};

const formatSendTimeLabel = (values: EmailCampaignFormValues): string => {
    if (values.sendTime === 'now') return 'Now';
    if (!values.scheduledDate || !values.scheduledTime) return 'Scheduled';
    const combined = moment(`${values.scheduledDate} ${values.scheduledTime}`, 'YYYY-MM-DD HH:mm', true);
    if (!combined.isValid()) return 'Scheduled';
    return combined.format('MMM D, YYYY [at] h:mm A');
};

export default CampaignReviewSummary;
